import { useState } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";
import { ProfileData } from "@/hooks/useProfileSettings";
import TelegramConnectionStatus from "./TelegramConnectionStatus";
import { useTranslation } from "react-i18next";

interface MessagingPlatformSelectorProps {
  profile: ProfileData | null;
  isLoading: boolean;
  connectingTelegram: boolean;
  connectionAttempted?: boolean;
  onPlatformChange: (platform: string) => void;
  onConnectTelegram: () => void;
  onOpenTelegramDirectly: () => void;
}

const MessagingPlatformSelector = ({
  profile,
  isLoading,
  connectingTelegram,
  connectionAttempted = false,
  onPlatformChange,
  onConnectTelegram,
  onOpenTelegramDirectly
}: MessagingPlatformSelectorProps) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const preferredMessaging = profile?.preferred_messaging || "whatsapp";

  const handleMessagingChange = async (value: string) => {
    setIsSaving(true);
    await onPlatformChange(value);
    setIsSaving(false);
  };

  return (
    <div className="space-y-2">
      <Label>{t('settings.preferredMessagingPlatform')}</Label>
      <div className="flex items-center">
        <RadioGroup 
          value={preferredMessaging}
          onValueChange={handleMessagingChange}
          className="flex justify-center space-x-8 py-2"
          disabled={isLoading || isSaving}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="whatsapp" id="whatsapp" />
            <Label htmlFor="whatsapp" className="cursor-pointer">{t('settings.whatsapp')}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="telegram" id="telegram" />
            <Label htmlFor="telegram" className="cursor-pointer">{t('settings.telegram')}</Label>
          </div>
        </RadioGroup>
        {isSaving && (
          <Loader2 className="ml-3 h-4 w-4 animate-spin text-gray-500" />
        )}
      </div>
      
      {preferredMessaging === "telegram" && (
        <TelegramConnectionStatus
          profile={profile}
          connectionAttempted={connectionAttempted}
          connectingTelegram={connectingTelegram}
          onConnectTelegram={onConnectTelegram}
          onOpenTelegramDirectly={onOpenTelegramDirectly}
        />
      )}
    </div>
  );
};

export default MessagingPlatformSelector;
